import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { LOGO_URL } from "../components/Navbar";
import { PawPrint, Store } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "provider" ? "provider" : "tutor");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("É necessário aceitar os Termos e a Política de Privacidade");
    setLoading(true);
    try {
      const user = await register({ ...form, role, consent_lgpd: consent, city: "Joinville" });
      toast.success("Conta criada! Bem-vindo(a) ao PetMachine Hub.");
      navigate(role === "provider" ? "/painel" : "/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-brand-coral/10 shadow-sm" data-testid="register-page">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="PetMachine Hub" className="h-12 w-auto mx-auto mb-4 rounded-lg" />
          <h1 className="font-heading font-bold text-2xl text-brand-graphite">Crie sua conta grátis</h1>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="role-selector">
          <button type="button" onClick={() => setRole("tutor")} data-testid="role-tutor-btn"
            className={`rounded-2xl border-2 p-4 text-center transition-all ${role === "tutor" ? "border-brand-coral bg-brand-coralLight" : "border-border"}`}>
            <PawPrint className={`w-6 h-6 mx-auto mb-1.5 ${role === "tutor" ? "text-brand-coral" : "text-muted-foreground"}`} />
            <p className="font-heading font-semibold text-sm">Sou Tutor</p>
          </button>
          <button type="button" onClick={() => setRole("provider")} data-testid="role-provider-btn"
            className={`rounded-2xl border-2 p-4 text-center transition-all ${role === "provider" ? "border-brand-mint bg-brand-mintLight" : "border-border"}`}>
            <Store className={`w-6 h-6 mx-auto mb-1.5 ${role === "provider" ? "text-brand-mintDark" : "text-muted-foreground"}`} />
            <p className="font-heading font-semibold text-sm">Sou Prestador</p>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome completo</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Seu nome" className="mt-1.5" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="voce@email.com" className="mt-1.5" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="phone">WhatsApp (opcional)</Label>
            <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(47) 99999-9999" className="mt-1.5" data-testid="register-phone-input" />
          </div>
          <div>
            <Label htmlFor="password">Senha</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Mínimo 6 caracteres" className="mt-1.5" data-testid="register-password-input" />
          </div>
          <div className="flex items-start gap-2.5">
            <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} data-testid="register-consent-checkbox" />
            <Label htmlFor="consent" className="text-xs text-muted-foreground leading-relaxed font-normal">
              Li e aceito os Termos de Uso e a Política de Privacidade, e autorizo o tratamento dos meus dados conforme a LGPD.
            </Label>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full h-11" data-testid="register-submit-btn">
            {loading ? "Criando conta..." : role === "provider" ? "Cadastrar e testar 7 dias grátis" : "Cadastrar grátis"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">ou</span><div className="h-px bg-border flex-1" />
        </div>
        <Button type="button" variant="outline" onClick={googleLogin} className="w-full rounded-full h-11" data-testid="google-register-btn">
          Continuar com Google
        </Button>
        <p className="text-sm text-center text-muted-foreground mt-6">
          Já tem conta? <Link to="/login" className="text-brand-coral font-semibold" data-testid="go-to-login">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
